import { BUILDINGS, TUNING } from '../core/defs';
import { DX, DY, type Building, type BuildingKind, type Dir, type Status } from '../core/types';
import { T_CONTEXT, T_REQUEST, T_RICH, T_ROCK, type Fx, type Jam, type World } from '../core/world';
import type { Blueprint } from '../game/commands';
import { hash2 } from '../core/rng';
import { DIM, INK, drawIcon, drawItem, recordColor, roundRect } from './icons';

export interface ViewState {
  tool: BuildingKind | null;
  toolDir: Dir;
  hover: { x: number; y: number } | null;
  /** Top-left tile for the current tool's footprint under the cursor. */
  place: { x: number; y: number } | null;
  dragPath: { x: number; y: number; dir: Dir }[] | null;
  removeRect: { x0: number; y0: number; x1: number; y1: number } | null;
  copyRect: { x0: number; y0: number; x1: number; y1: number } | null;
  selectedId: number | null;
  showLabels: boolean;
  stamp: Blueprint | null;
  planMode: boolean;
  reducedMotion: boolean;
  showDebt: boolean;
}

/** When causes are hidden, lights only say working, waiting, stopped or misbehaving. */
const SYMPTOM_COLOR: Record<Status, string> = {
  ok: '#59D98E', idle: '#55657A', starved: '#55657A', blocked: '#C9B27A', nopower: '#C9B27A', corrupt: '#FF3DCE', tripped: '#C9B27A', ghost: '#8FA0B3',
};

export const STATUS_COLOR: Record<Status, string> = {
  ok: '#59D98E', idle: '#55657A', starved: '#F2B84B', blocked: '#FF9F5A', nopower: '#E0564B', corrupt: '#FF3DCE', tripped: '#F2D27A', ghost: '#8FA0B3',
};

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: string;
  size: number;
  kind: 'dot' | 'ring' | 'line' | 'square';
  x2?: number;
  y2?: number;
}

export class Camera {
  cx = 0;
  cy = 0;
  zoom = 30;
  targetZoom = 30;
  private tx: number | null = null;
  private ty: number | null = null;
  shake = 0;
  vw = 1;
  vh = 1;

  toScreen(x: number, y: number): [number, number] {
    return [(x - this.cx) * this.zoom + this.vw / 2, (y - this.cy) * this.zoom + this.vh / 2];
  }

  toWorld(sx: number, sy: number): [number, number] {
    return [(sx - this.vw / 2) / this.zoom + this.cx, (sy - this.vh / 2) / this.zoom + this.cy];
  }

  focus(x: number, y: number) {
    this.tx = x;
    this.ty = y;
  }

  pan(dx: number, dy: number) {
    this.cx += dx;
    this.cy += dy;
    this.tx = this.ty = null;
  }

  zoomAt(factor: number, sx: number, sy: number) {
    const [wx, wy] = this.toWorld(sx, sy);
    this.zoom = Math.max(9, Math.min(72, this.zoom * factor));
    this.targetZoom = this.zoom;
    const [nx, ny] = this.toWorld(sx, sy);
    this.cx += wx - nx;
    this.cy += wy - ny;
  }

  update(dt: number, w: World) {
    if (this.tx !== null && this.ty !== null) {
      const k = 1 - Math.pow(0.0015, dt);
      this.cx += (this.tx - this.cx) * k;
      this.cy += (this.ty - this.cy) * k;
      if (Math.hypot(this.tx - this.cx, this.ty - this.cy) < 0.02) this.tx = this.ty = null;
    }
    this.cx = Math.max(-4, Math.min(w.w + 4, this.cx));
    this.cy = Math.max(-4, Math.min(w.h + 4, this.cy));
    this.shake = Math.max(0, this.shake - dt * 2.2);
  }
}

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  readonly cam = new Camera();
  private parts: Particle[] = [];
  private dpr = 1;
  private jams: Jam[] = [];
  private jamFrame = 0;
  time = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: false })!;
    this.resize();
  }

  resize() {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.cam.vw = w;
    this.cam.vh = h;
  }

  /** Turn simulation effects into particles. Returns true if the screen should shake. */
  addFx(f: Fx, reduced: boolean) {
    const P = (p: Partial<Particle> & { x: number; y: number }) =>
      this.parts.push({ vx: 0, vy: 0, life: 0, max: 0.6, color: '#fff', size: 0.1, kind: 'dot', ...p });
    const burst = (n: number, color: string, speed: number, size: number, max = 0.7) => {
      const count = reduced ? Math.ceil(n / 3) : n;
      for (let i = 0; i < count; i++) {
        const a = Math.random() * Math.PI * 2;
        const v = speed * (0.4 + Math.random() * 0.8);
        P({ x: f.x, y: f.y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, color, size: size * (0.6 + Math.random() * 0.7), max: max * (0.7 + Math.random() * 0.6), kind: Math.random() < 0.5 ? 'square' : 'dot' });
      }
    };
    switch (f.type) {
      case 'place':
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(200,220,255,0.6)', size: 0.6, max: 0.35 });
        break;
      case 'remove':
        burst(6, '#8FA0B3', 2.5, 0.08, 0.4);
        break;
      case 'release':
        burst(5, '#9CF2C0', 2.2, 0.08, 0.6);
        break;
      case 'flawed':
        burst(f.big ? 22 : 12, '#FF8A7A', 3.5, 0.12, 0.8);
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(255,107,107,0.8)', size: f.big ? 4 : 2.5, max: 0.6 });
        break;
      case 'corrupt':
        burst(18, '#FF3DCE', 3, 0.12, 0.9);
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(255,61,206,0.8)', size: 2.4, max: 0.5 });
        break;
      case 'zap':
        P({ x: f.x, y: f.y, x2: f.x2, y2: f.y2, kind: 'line', color: 'rgba(127,227,255,0.95)', size: 0.08, max: 0.12 });
        break;
      case 'glitchDie':
        burst(8, '#FF7AE0', 2.4, 0.09, 0.5);
        break;
      case 'spawn':
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(255,61,206,0.6)', size: 1.2, max: 0.45 });
        break;
      case 'rollback':
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(127,214,255,0.9)', size: TUNING.checkpointRadius * 2, max: 0.9 });
        burst(20, '#7FD6FF', 4, 0.1, 0.8);
        break;
      case 'trip':
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(242,210,122,0.9)', size: 1.6, max: 0.45 });
        burst(8, '#F2D27A', 2.5, 0.09, 0.5);
        break;
      case 'secret':
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(242,210,122,0.9)', size: 5, max: 1.1 });
        burst(24, '#F2D27A', 3, 0.1, 1.1);
        break;
      case 'nestDie':
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(156,242,192,0.9)', size: 7, max: 1 });
        burst(40, '#FF3DCE', 6, 0.14, 1.2);
        break;
      case 'protect':
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(127,214,255,0.9)', size: 6, max: 0.9 });
        if (f.x2 !== undefined && f.y2 !== undefined) P({ x: f.x, y: f.y, x2: f.x2, y2: f.y2, kind: 'line', color: 'rgba(127,214,255,0.8)', size: 0.12, max: 0.6 });
        break;
      case 'backfire':
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(255,61,206,0.95)', size: 14, max: 0.9 });
        burst(40, '#FF3DCE', 7, 0.14, 1.1);
        if (!reduced) this.cam.shake = 1;
        break;
      case 'approval':
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(89,217,142,0.9)', size: 4, max: 0.8 });
        break;
      case 'denied':
        P({ x: f.x, y: f.y, kind: 'ring', color: 'rgba(224,86,75,0.9)', size: 4, max: 0.8 });
        break;
      case 'patch':
        burst(10, '#7FD6FF', 2, 0.09, 0.6);
        break;
      case 'drone':
        burst(6, '#D6B8FF', 1.6, 0.08, 0.4);
        break;
      case 'research':
        break;
    }
    if ((f.type === 'corrupt' || f.type === 'flawed') && f.big && !reduced) this.cam.shake = Math.max(this.cam.shake, 0.45);
    if (this.parts.length > 1500) this.parts.splice(0, this.parts.length - 1500);
  }

  render(w: World, v: ViewState, alpha: number, dt: number) {
    this.time += dt;
    const ctx = this.ctx;
    const cam = this.cam;
    cam.update(dt, w);
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = '#080B10';
    ctx.fillRect(0, 0, cam.vw, cam.vh);
    let sx = 0;
    let sy = 0;
    if (cam.shake > 0 && !v.reducedMotion) {
      sx = (Math.random() - 0.5) * cam.shake * 10;
      sy = (Math.random() - 0.5) * cam.shake * 10;
    }
    ctx.save();
    ctx.translate(sx, sy);
    const z = cam.zoom;
    const [wx0, wy0] = cam.toWorld(0, 0);
    const [wx1, wy1] = cam.toWorld(cam.vw, cam.vh);
    const x0 = Math.max(0, Math.floor(wx0) - 1);
    const y0 = Math.max(0, Math.floor(wy0) - 1);
    const x1 = Math.min(w.w - 1, Math.ceil(wx1) + 1);
    const y1 = Math.min(w.h - 1, Math.ceil(wy1) + 1);
    const S = (x: number, y: number) => cam.toScreen(x, y);

    this.drawTerrain(w, x0, y0, x1, y1, z);
    this.drawZones(w, z);
    if (v.showDebt || w.level.flags.threats) this.drawDebt(w, v.showDebt);
    this.drawBelts(w, x0, y0, x1, y1, z, alpha);
    this.drawBuildings(w, v, x0, y0, x1, y1, z);
    this.drawDrones(w, z);
    this.drawGlitches(w, z);
    this.drawParticles(dt, z);
    this.drawFloats(w, z);
    this.drawFog(w, x0, y0, x1, y1, z);
    if (w.marks.size) this.drawMarks(w, z);
    if (++this.jamFrame % 6 === 0) this.jams = w.jams();
    if (this.jams.length) this.drawJams(z);
    this.drawOverlays(w, v, z);
    if (v.showLabels || z >= 34) this.drawLabels(w, x0, y0, x1, y1, z, v.showLabels);
    // Map edge.
    const [ex, ey] = S(0, 0);
    ctx.strokeStyle = 'rgba(120,140,170,0.25)';
    ctx.lineWidth = 1;
    ctx.strokeRect(ex, ey, w.w * z, w.h * z);
    ctx.restore();
  }

  private drawTerrain(w: World, x0: number, y0: number, x1: number, y1: number, z: number) {
    const ctx = this.ctx;
    const seed = w.level.seed;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const i = y * w.w + x;
        if (!w.fog[i]) continue;
        const t = w.terrain[i];
        const [px, py] = this.cam.toScreen(x, y);
        if (t === T_ROCK) {
          ctx.fillStyle = '#161B22';
          ctx.fillRect(px, py, z + 0.5, z + 0.5);
          if (z > 14) {
            ctx.fillStyle = '#1E252E';
            const n = hash2(x, y, seed);
            ctx.beginPath();
            ctx.arc(px + z * (0.3 + n * 0.4), py + z * (0.35 + hash2(y, x, seed) * 0.3), z * (0.18 + n * 0.12), 0, Math.PI * 2);
            ctx.fill();
          }
          continue;
        }
        const n = hash2(x, y, seed + 11);
        if (t === T_REQUEST) {
          ctx.fillStyle = '#211B10';
          ctx.fillRect(px, py, z + 0.5, z + 0.5);
          if (z > 12) {
            ctx.fillStyle = 'rgba(242,184,75,0.55)';
            ctx.beginPath();
            ctx.arc(px + z * (0.3 + n * 0.4), py + z * (0.3 + hash2(y, x, seed) * 0.4), z * 0.09, 0, Math.PI * 2);
            ctx.fill();
          }
        } else if (t === T_CONTEXT || t === T_RICH) {
          ctx.fillStyle = t === T_RICH ? '#0F2E31' : '#0D2224';
          ctx.fillRect(px, py, z + 0.5, z + 0.5);
          if (z > 12) {
            const cx = px + z * (0.3 + n * 0.4);
            const cy = py + z * (0.3 + hash2(y, x, seed) * 0.4);
            const r = z * (t === T_RICH ? 0.14 : 0.1);
            ctx.fillStyle = t === T_RICH ? 'rgba(90,240,215,0.75)' : 'rgba(60,200,180,0.55)';
            ctx.beginPath();
            ctx.moveTo(cx, cy - r);
            ctx.lineTo(cx + r * 0.8, cy);
            ctx.lineTo(cx, cy + r);
            ctx.lineTo(cx - r * 0.8, cy);
            ctx.fill();
          }
        } else {
          ctx.fillStyle = n < 0.5 ? '#0C1117' : '#0D1218';
          ctx.fillRect(px, py, z + 0.5, z + 0.5);
        }
      }
    }
    if (z >= 16) {
      ctx.strokeStyle = 'rgba(140,160,190,0.06)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = x0; x <= x1 + 1; x++) {
        const [px, py] = this.cam.toScreen(x, y0);
        ctx.moveTo(Math.round(px) + 0.5, py);
        ctx.lineTo(Math.round(px) + 0.5, py + (y1 - y0 + 1) * z);
      }
      for (let y = y0; y <= y1 + 1; y++) {
        const [px, py] = this.cam.toScreen(x0, y);
        ctx.moveTo(px, Math.round(py) + 0.5);
        ctx.lineTo(px + (x1 - x0 + 1) * z, Math.round(py) + 0.5);
      }
      ctx.stroke();
    }
  }

  private drawZones(w: World, z: number) {
    const ctx = this.ctx;
    for (const zone of w.zones) {
      const [px, py] = this.cam.toScreen(zone.x0, zone.y0);
      const ww = (zone.x1 - zone.x0 + 1) * z;
      const hh = (zone.y1 - zone.y0 + 1) * z;
      const open = zone.state === 'approved' || zone.state === 'claimed';
      ctx.save();
      ctx.beginPath();
      ctx.rect(px, py, ww, hh);
      ctx.clip();
      ctx.strokeStyle = open ? 'rgba(89,217,142,0.07)' : 'rgba(150,120,255,0.13)';
      ctx.lineWidth = Math.max(1, z * 0.08);
      ctx.beginPath();
      const step = z * 1.2;
      for (let d = -hh; d < ww; d += step) {
        ctx.moveTo(px + d, py + hh);
        ctx.lineTo(px + d + hh, py);
      }
      ctx.stroke();
      ctx.restore();
      ctx.setLineDash([z * 0.4, z * 0.3]);
      ctx.strokeStyle = open ? 'rgba(89,217,142,0.45)' : 'rgba(170,140,255,0.6)';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(px, py, ww, hh);
      ctx.setLineDash([]);
    }
  }

  private drawDebt(w: World, strong: boolean) {
    const ctx = this.ctx;
    const z = this.cam.zoom;
    for (let cy = 0; cy < w.chh; cy++) {
      for (let cx = 0; cx < w.cw; cx++) {
        const d = w.debt[cy * w.cw + cx];
        if (d < 2) continue;
        const a = Math.min(strong ? 0.35 : 0.16, d / (strong ? 120 : 260));
        const [px, py] = this.cam.toScreen(cx * 8, cy * 8);
        ctx.fillStyle = `rgba(190,60,170,${a})`;
        ctx.fillRect(px, py, 8 * z, 8 * z);
      }
    }
  }

  private drawBelts(w: World, x0: number, y0: number, x1: number, y1: number, z: number, alpha: number) {
    const ctx = this.ctx;
    const t = this.time;
    const speed = TUNING.beltSpeed * (w.techs.has('belts') ? 1.5 : 1);
    const items: [Building, number][] = [];
    for (const b of w.buildings.values()) {
      if (b.kind !== 'belt' && b.kind !== 'breaker' && b.kind !== 'gate') continue;
      if (b.x < x0 || b.x > x1 || b.y < y0 || b.y > y1) continue;
      if (!w.fog[b.y * w.w + b.x]) continue;
      const [px, py] = this.cam.toScreen(b.x, b.y);
      const ghost = b.ghost;
      ctx.globalAlpha = ghost ? 0.35 : 1;
      ctx.fillStyle = b.kind === 'breaker' ? (b.tripped ? '#4A3A12' : '#2A2416') : b.kind === 'gate' ? '#12303A' : '#18212C';
      ctx.fillRect(px + 0.5, py + 0.5, z - 1, z - 1);
      // Rails along the direction of travel.
      ctx.fillStyle = b.kind === 'breaker' ? '#6B5A2A' : b.kind === 'gate' ? '#2C6A72' : '#26364A';
      const horiz = b.dir === 0 || b.dir === 2;
      if (horiz) {
        ctx.fillRect(px, py + z * 0.08, z, z * 0.07);
        ctx.fillRect(px, py + z * 0.85, z, z * 0.07);
      } else {
        ctx.fillRect(px + z * 0.08, py, z * 0.07, z);
        ctx.fillRect(px + z * 0.85, py, z * 0.07, z);
      }
      if (z >= 12) {
        const off = ghost || b.tripped ? 0 : (t * speed) % 0.5;
        ctx.strokeStyle = b.kind === 'breaker' ? 'rgba(242,210,122,0.55)' : 'rgba(110,140,175,0.42)';
        ctx.lineWidth = Math.max(1, z * 0.07);
        ctx.beginPath();
        for (let k = 0; k < 2; k++) {
          const p = off + k * 0.5 - 0.25;
          const cx = px + z / 2 + DX[b.dir] * (p) * z;
          const cy = py + z / 2 + DY[b.dir] * (p) * z;
          const s = z * 0.16;
          const ax = DX[b.dir];
          const ay = DY[b.dir];
          // chevron pointing in travel direction
          ctx.moveTo(cx - ax * s - ay * s, cy - ay * s + ax * s);
          ctx.lineTo(cx, cy);
          ctx.lineTo(cx - ax * s + ay * s, cy - ay * s - ax * s);
        }
        ctx.stroke();
      }
      if (b.kind === 'gate') drawIcon(ctx, 'gate', px + z / 2, py + z / 2, z * 0.62, t, true);
      if (b.kind === 'breaker') {
        drawIcon(ctx, 'breaker', px + z / 2, py + z / 2, z * 0.7, t, true);
        if (b.tripped) {
          ctx.strokeStyle = `rgba(242,210,122,${0.6 + Math.sin(t * 10) * 0.4})`;
          ctx.lineWidth = 2;
          ctx.strokeRect(px + 1, py + 1, z - 2, z - 2);
        }
      }
      ctx.globalAlpha = 1;
      if (b.slots.length) items.push([b, px]);
    }
    const vstep = speed / 30;
    for (const [b] of items) {
      const [px, py] = this.cam.toScreen(b.x, b.y);
      for (let i = 0; i < b.slots.length; i++) {
        const s = b.slots[i];
        let p = s.p;
        if (!b.tripped) {
          const lim = i === 0 ? 1 : b.slots[i - 1].p - TUNING.beltGap;
          p = Math.min(Math.max(p, Math.min(lim, p + vstep * alpha)), 1);
        }
        const cx = px + z / 2 + DX[b.dir] * (p - 0.5) * z;
        const cy = py + z / 2 + DY[b.dir] * (p - 0.5) * z;
        if (z < 11) {
          ctx.fillStyle = s.item.corrupt ? '#FF3DCE' : '#ccc';
          ctx.fillRect(cx - 1.5, cy - 1.5, 3, 3);
        } else {
          drawItem(ctx, s.item.kind, cx, cy, z * 0.2, !!s.item.corrupt, t);
          if (w.concurrency && s.item.rec && z >= 14 && s.item.kind !== 'context') {
            ctx.strokeStyle = recordColor(s.item.rec);
            ctx.lineWidth = Math.max(1, z * 0.05);
            ctx.beginPath();
            ctx.arc(cx, cy, z * 0.27, 0, Math.PI * 2);
            ctx.stroke();
          }
        }
      }
    }
  }

  private drawBuildings(w: World, v: ViewState, x0: number, y0: number, x1: number, y1: number, z: number) {
    const ctx = this.ctx;
    const t = this.time;
    for (const b of w.buildings.values()) {
      if (b.kind === 'belt' || b.kind === 'breaker' || b.kind === 'gate') continue;
      if (b.x + b.w < x0 || b.x > x1 || b.y + b.h < y0 || b.y > y1) continue;
      if (!w.revealed(b.x, b.y) && !w.revealed(b.x + b.w - 1, b.y + b.h - 1)) continue;
      const [px, py] = this.cam.toScreen(b.x, b.y);
      const bw = b.w * z;
      const bh = b.h * z;
      const def = BUILDINGS[b.kind];
      const inset = z * 0.07;
      ctx.globalAlpha = b.ghost ? 0.4 : 1;
      if (b.kind === 'firewall') {
        ctx.fillStyle = '#2C3138';
        ctx.fillRect(px + 1, py + 1, bw - 2, bh - 2);
        drawIcon(ctx, 'firewall', px + bw / 2, py + bh / 2, z * 0.8, t, false);
        if (b.hp < b.maxHp) {
          ctx.fillStyle = '#E0564B';
          ctx.fillRect(px + 2, py + bh - 4, (bw - 4) * (b.hp / b.maxHp), 2);
        }
        ctx.globalAlpha = 1;
        continue;
      }
      if (b.kind === 'router') {
        ctx.fillStyle = '#1E2B3A';
        roundRect(ctx, px + 1, py + 1, bw - 2, bh - 2, z * 0.2);
        ctx.fill();
        drawIcon(ctx, 'router', px + bw / 2, py + bh / 2, z * 0.6, t, true);
        ctx.globalAlpha = 1;
        continue;
      }
      // Body.
      roundRect(ctx, px + inset, py + inset, bw - inset * 2, bh - inset * 2, z * 0.22);
      ctx.fillStyle = def.color;
      ctx.fill();
      ctx.strokeStyle = b.id === v.selectedId ? '#FFFFFF' : 'rgba(255,255,255,0.12)';
      ctx.lineWidth = b.id === v.selectedId ? 2 : 1;
      if (b.ghost) ctx.setLineDash([4, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
      if (b.kind === 'users') {
        // Soft halo so the destination reads from anywhere.
        ctx.strokeStyle = `rgba(89,217,142,${0.25 + Math.sin(t * 2) * 0.1})`;
        ctx.lineWidth = 2;
        roundRect(ctx, px - 3, py - 3, bw + 6, bh + 6, z * 0.3);
        ctx.stroke();
      }
      const active = b.status === 'ok';
      if (z >= 10) drawIcon(ctx, b.kind, px + bw / 2, py + bh / 2 - (b.kind === 'monolith' ? z * 0.4 : 0), Math.min(bw, bh) * 0.62, t, active);
      // Output arrows.
      if (def.placeable && b.kind !== 'rack' && b.kind !== 'monitor' && b.kind !== 'checkpoint' && b.kind !== 'scanner' && b.kind !== 'drones' && b.kind !== 'postmortem') {
        this.arrow(px, py, bw, bh, b.dir, '#C9D6E6', z);
        if (b.kind === 'bench') this.arrow(px, py, bw, bh, ((b.dir + 3) % 4) as Dir, '#FF6B6B', z);
      }
      // Progress bar.
      if (b.crafting && def.craftTime > 0) {
        const p = Math.min(1, b.progress / def.craftTime);
        ctx.fillStyle = 'rgba(0,0,0,0.35)';
        ctx.fillRect(px + inset * 2, py + bh - inset * 2 - z * 0.09, bw - inset * 4, z * 0.09);
        ctx.fillStyle = b.kind === 'bench' ? '#7CF0B0' : b.kind === 'forge' ? '#C9A8FF' : '#9DB8DA';
        ctx.fillRect(px + inset * 2, py + bh - inset * 2 - z * 0.09, (bw - inset * 4) * p, z * 0.09);
      }
      if (b.kind === 'indexer' && b.status === 'ok') {
        const p = b.progress / BUILDINGS.indexer.craftTime;
        ctx.fillStyle = 'rgba(242,184,75,0.5)';
        ctx.fillRect(px + inset * 2, py + bh - inset * 2 - z * 0.08, (bw - inset * 4) * Math.min(1, p), z * 0.08);
      }
      if (b.kind === 'checkpoint' && b.cooldown > 0) {
        ctx.strokeStyle = 'rgba(127,214,255,0.8)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px + bw / 2, py + bh / 2, z * 0.75, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * (1 - b.cooldown / TUNING.checkpointCooldown));
        ctx.stroke();
      }
      if (b.kind === 'nest' && b.hp < b.maxHp) {
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        ctx.fillRect(px + 4, py - 6, bw - 8, 4);
        ctx.fillStyle = '#FF3DCE';
        ctx.fillRect(px + 4, py - 6, (bw - 8) * Math.max(0, b.hp / b.maxHp), 4);
      }
      // Status light.
      if (def.placeable && !b.ghost) {
        ctx.fillStyle = (w.level.flags.diagnoses === false ? SYMPTOM_COLOR : STATUS_COLOR)[b.status] ?? DIM;
        ctx.beginPath();
        ctx.arc(px + bw - inset - z * 0.16, py + inset + z * 0.16, Math.max(2, z * 0.07), 0, Math.PI * 2);
        ctx.fill();
      }
      if (b.corrupted) this.corruptOverlay(px, py, bw, bh, t);
      ctx.globalAlpha = 1;
    }
  }

  private arrow(px: number, py: number, bw: number, bh: number, d: Dir, color: string, z: number) {
    const ctx = this.ctx;
    const cx = px + bw / 2 + DX[d] * (bw / 2 - z * 0.12);
    const cy = py + bh / 2 + DY[d] * (bh / 2 - z * 0.12);
    const s = z * 0.13;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(cx + DX[d] * s, cy + DY[d] * s);
    ctx.lineTo(cx - DX[d] * s * 0.6 - DY[d] * s, cy - DY[d] * s * 0.6 + DX[d] * s);
    ctx.lineTo(cx - DX[d] * s * 0.6 + DY[d] * s, cy - DY[d] * s * 0.6 - DX[d] * s);
    ctx.closePath();
    ctx.fill();
  }

  private corruptOverlay(px: number, py: number, bw: number, bh: number, t: number) {
    const ctx = this.ctx;
    ctx.fillStyle = `rgba(255,61,206,${0.18 + Math.sin(t * 9) * 0.08})`;
    ctx.fillRect(px, py, bw, bh);
    for (let i = 0; i < 6; i++) {
      const n = hash2(i, Math.floor(t * 12), 99);
      ctx.fillStyle = i % 2 ? 'rgba(255,61,206,0.8)' : 'rgba(255,255,255,0.55)';
      ctx.fillRect(px + n * bw * 0.85, py + hash2(Math.floor(t * 12), i, 7) * bh * 0.9, bw * 0.15, Math.max(2, bh * 0.05));
    }
  }

  private drawDrones(w: World, z: number) {
    const ctx = this.ctx;
    for (const d of w.drones) {
      if (!w.revealed(Math.floor(d.x), Math.floor(d.y))) continue;
      const [px, py] = this.cam.toScreen(d.x, d.y);
      ctx.fillStyle = d.task === 'patch' ? '#7FD6FF' : '#D6B8FF';
      ctx.beginPath();
      const s = Math.max(2.5, z * 0.14);
      ctx.moveTo(px, py - s);
      ctx.lineTo(px + s, py);
      ctx.lineTo(px, py + s);
      ctx.lineTo(px - s, py);
      ctx.fill();
    }
  }

  private drawGlitches(w: World, z: number) {
    const ctx = this.ctx;
    const t = this.time;
    for (const g of w.glitches) {
      const [px, py] = this.cam.toScreen(g.x, g.y);
      const s = Math.max(3, z * 0.42);
      for (let i = 0; i < 4; i++) {
        const jx = Math.sin(t * 31 + g.wob * 7 + i * 2.1) * s * 0.35;
        const jy = Math.cos(t * 27 + g.wob * 5 + i * 1.3) * s * 0.35;
        ctx.fillStyle = i === 0 ? '#FF3DCE' : i === 1 ? 'rgba(255,61,206,0.6)' : i === 2 ? 'rgba(120,240,255,0.55)' : 'rgba(255,255,255,0.7)';
        const q = s * (i === 0 ? 0.5 : 0.3);
        ctx.fillRect(px + jx - q / 2, py + jy - q / 2, q, q);
      }
      if (g.attackT > 0) {
        ctx.strokeStyle = 'rgba(255,61,206,0.7)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(px, py, s * 0.7, -Math.PI / 2, -Math.PI / 2 + (g.attackT / TUNING.glitchAttackTime) * Math.PI * 2);
        ctx.stroke();
      }
    }
  }

  private drawParticles(dt: number, z: number) {
    const ctx = this.ctx;
    const keep: Particle[] = [];
    for (const p of this.parts) {
      p.life += dt;
      if (p.life >= p.max) continue;
      keep.push(p);
      const k = 1 - p.life / p.max;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.92;
      p.vy *= 0.92;
      const [px, py] = this.cam.toScreen(p.x, p.y);
      ctx.globalAlpha = Math.max(0, k);
      if (p.kind === 'ring') {
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px, py, (p.size / 2) * z * (1 - k * 0.7), 0, Math.PI * 2);
        ctx.stroke();
      } else if (p.kind === 'line' && p.x2 !== undefined && p.y2 !== undefined) {
        const [qx, qy] = this.cam.toScreen(p.x2, p.y2);
        ctx.strokeStyle = p.color;
        ctx.lineWidth = Math.max(1.5, p.size * z);
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(qx, qy);
        ctx.stroke();
      } else {
        ctx.fillStyle = p.color;
        const s = Math.max(1.5, p.size * z * k);
        if (p.kind === 'square') ctx.fillRect(px - s / 2, py - s / 2, s, s);
        else {
          ctx.beginPath();
          ctx.arc(px, py, s / 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.globalAlpha = 1;
    this.parts = keep;
  }

  private drawFloats(w: World, z: number) {
    const ctx = this.ctx;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `600 ${Math.round(Math.max(11, Math.min(16, z * 0.45)))}px "Space Grotesk Variable", ui-sans-serif, system-ui, sans-serif`;
    for (const f of w.floats) {
      const [px, py] = this.cam.toScreen(f.x, f.y - f.t * 1.2);
      ctx.globalAlpha = Math.max(0, 1 - f.t / 1.6);
      ctx.fillStyle = 'rgba(8,11,16,0.7)';
      const tw = ctx.measureText(f.text).width;
      roundRect(ctx, px - tw / 2 - 6, py - 10, tw + 12, 20, 6);
      ctx.fill();
      ctx.fillStyle = f.color;
      ctx.fillText(f.text, px, py + 1);
    }
    ctx.globalAlpha = 1;
  }

  private drawFog(w: World, x0: number, y0: number, x1: number, y1: number, z: number) {
    const ctx = this.ctx;
    ctx.fillStyle = '#05070A';
    for (let y = y0; y <= y1; y++) {
      let run = -1;
      for (let x = x0; x <= x1 + 1; x++) {
        const fogged = x <= x1 && !w.fog[y * w.w + x];
        if (fogged && run < 0) run = x;
        if (!fogged && run >= 0) {
          const [px, py] = this.cam.toScreen(run, y);
          ctx.fillRect(px, py, (x - run) * z + 0.5, z + 0.5);
          run = -1;
        }
      }
    }
    // Soft edge: darken revealed tiles that touch the fog.
    if (z >= 8) {
      ctx.fillStyle = 'rgba(5,7,10,0.45)';
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const i = y * w.w + x;
          if (!w.fog[i]) continue;
          const edge = (x > 0 && !w.fog[i - 1]) || (x < w.w - 1 && !w.fog[i + 1]) || (y > 0 && !w.fog[i - w.w]) || (y < w.h - 1 && !w.fog[i + w.w]);
          if (edge) {
            const [px, py] = this.cam.toScreen(x, y);
            ctx.fillRect(px, py, z + 0.5, z + 0.5);
          }
        }
      }
    }
  }

  private drawOverlays(w: World, v: ViewState, z: number) {
    const ctx = this.ctx;
    const circle = (x: number, y: number, r: number, color: string, fill?: string) => {
      const [px, py] = this.cam.toScreen(x, y);
      ctx.beginPath();
      ctx.arc(px, py, r * z, 0, Math.PI * 2);
      if (fill) {
        ctx.fillStyle = fill;
        ctx.fill();
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
    };
    const tool = v.tool;
    // Compute coverage while placing anything that needs it.
    if (tool && (BUILDINGS[tool].compute > 0 || tool === 'rack')) {
      for (const b of w.buildings.values()) if (b.kind === 'rack') circle(b.x + 1, b.y + 1, TUNING.rackRadius, 'rgba(242,210,122,0.35)', 'rgba(242,210,122,0.04)');
    }
    if (tool === 'checkpoint' || v.tool === null) {
      for (const b of w.buildings.values()) if (b.kind === 'checkpoint' && (tool === 'checkpoint' || b.id === v.selectedId)) circle(b.x + 1, b.y + 1, TUNING.checkpointRadius, 'rgba(127,214,255,0.45)', 'rgba(127,214,255,0.04)');
    }
    const sel = v.selectedId ? w.buildings.get(v.selectedId) : undefined;
    if (sel) {
      const r = rangeOf(sel.kind, w);
      if (r) circle(sel.x + sel.w / 2, sel.y + sel.h / 2, r, 'rgba(200,220,255,0.4)', 'rgba(200,220,255,0.03)');
    }
    // Placement preview.
    if (v.place && tool && !v.dragPath) {
      const def = BUILDINGS[tool];
      const { x, y } = v.place;
      const c = w.canPlace(tool, x, y, v.planMode);
      const [px, py] = this.cam.toScreen(x, y);
      ctx.globalAlpha = 0.85;
      roundRect(ctx, px + 2, py + 2, def.w * z - 4, def.h * z - 4, z * 0.2);
      ctx.fillStyle = c.ok ? 'rgba(89,217,142,0.16)' : c.zone ? 'rgba(170,140,255,0.2)' : 'rgba(224,86,75,0.18)';
      ctx.fill();
      ctx.strokeStyle = c.ok ? 'rgba(89,217,142,0.85)' : c.zone ? 'rgba(170,140,255,0.9)' : 'rgba(224,86,75,0.85)';
      ctx.lineWidth = 2;
      if (v.planMode) ctx.setLineDash([5, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
      if (z >= 10) drawIcon(ctx, tool, px + (def.w * z) / 2, py + (def.h * z) / 2, Math.min(def.w, def.h) * z * 0.55, this.time, false);
      if (tool !== 'firewall' && tool !== 'rack' && tool !== 'monitor' && tool !== 'checkpoint' && tool !== 'scanner' && tool !== 'drones' && tool !== 'postmortem') {
        this.arrow(px, py, def.w * z, def.h * z, v.toolDir, '#FFFFFF', z);
        if (tool === 'bench') this.arrow(px, py, def.w * z, def.h * z, ((v.toolDir + 3) % 4) as Dir, '#FF6B6B', z);
      }
      ctx.globalAlpha = 1;
      const r = rangeOf(tool, w);
      if (r) circle(x + def.w / 2, y + def.h / 2, r, 'rgba(200,220,255,0.45)', 'rgba(200,220,255,0.04)');
    }
    if (v.dragPath) {
      for (const s of v.dragPath) {
        const [px, py] = this.cam.toScreen(s.x, s.y);
        const ok = w.canPlace('belt', s.x, s.y, v.planMode).ok;
        ctx.fillStyle = ok ? 'rgba(89,217,142,0.22)' : 'rgba(224,86,75,0.25)';
        ctx.fillRect(px + 2, py + 2, z - 4, z - 4);
        this.arrow(px, py, z, z, s.dir, ok ? '#9CF2C0' : '#FF8A7A', z);
      }
    }
    if (v.stamp && v.hover) {
      const ox = v.hover.x - Math.floor(v.stamp.w / 2);
      const oy = v.hover.y - Math.floor(v.stamp.h / 2);
      for (const it of v.stamp.items) {
        const def = BUILDINGS[it.kind];
        const [px, py] = this.cam.toScreen(ox + it.dx, oy + it.dy);
        const ok = w.canPlace(it.kind, ox + it.dx, oy + it.dy, true).ok;
        ctx.fillStyle = ok ? 'rgba(214,184,255,0.22)' : 'rgba(224,86,75,0.25)';
        ctx.fillRect(px + 1, py + 1, def.w * z - 2, def.h * z - 2);
      }
    }
    const rect = v.removeRect ?? v.copyRect;
    if (rect) {
      const xa = Math.min(rect.x0, rect.x1);
      const ya = Math.min(rect.y0, rect.y1);
      const [px, py] = this.cam.toScreen(xa, ya);
      const ww = (Math.abs(rect.x1 - rect.x0) + 1) * z;
      const hh = (Math.abs(rect.y1 - rect.y0) + 1) * z;
      ctx.fillStyle = v.removeRect ? 'rgba(224,86,75,0.12)' : 'rgba(214,184,255,0.12)';
      ctx.fillRect(px, py, ww, hh);
      ctx.strokeStyle = v.removeRect ? 'rgba(224,86,75,0.8)' : 'rgba(214,184,255,0.85)';
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 1.5;
      ctx.strokeRect(px, py, ww, hh);
      ctx.setLineDash([]);
    }
    if (v.hover && !tool && z >= 14) {
      const [px, py] = this.cam.toScreen(v.hover.x, v.hover.y);
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.lineWidth = 1;
      ctx.strokeRect(px + 0.5, py + 0.5, z - 1, z - 1);
    }
  }

  /** A red stop sign where a belt's work can't go any further. */
  private drawJams(z: number) {
    const ctx = this.ctx;
    const pulse = 0.6 + Math.sin(this.time * 6) * 0.3;
    for (const j of this.jams) {
      const b = j.from;
      const [px, py] = this.cam.toScreen(b.x, b.y);
      const bw = b.w * z;
      const bh = b.h * z;
      ctx.strokeStyle = `rgba(224,86,75,${pulse * 0.8})`;
      ctx.lineWidth = 2;
      ctx.strokeRect(px + 1, py + 1, bw - 2, bh - 2);
      const cx = px + bw / 2 + (DX[b.dir] * bw) / 2;
      const cy = py + bh / 2 + (DY[b.dir] * bh) / 2;
      const r = Math.max(7, z * 0.3);
      ctx.fillStyle = `rgba(224,86,75,${0.75 + pulse * 0.25})`;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.fillRect(cx - r * 0.55, cy - r * 0.16, r * 1.1, r * 0.32);
    }
  }

  private drawMarks(w: World, z: number) {
    const ctx = this.ctx;
    const pulse = 0.65 + Math.sin(this.time * 4) * 0.25;
    for (const id of w.marks) {
      const b = w.buildings.get(id);
      if (!b) continue;
      const [px, py] = this.cam.toScreen(b.x, b.y);
      ctx.strokeStyle = `rgba(242,210,122,${pulse})`;
      ctx.lineWidth = 2.5;
      ctx.setLineDash([5, 4]);
      ctx.strokeRect(px - 3, py - 3, b.w * z + 6, b.h * z + 6);
      ctx.setLineDash([]);
      ctx.fillStyle = '#F2D27A';
      ctx.beginPath();
      ctx.moveTo(px + b.w * z + 2, py - 3);
      ctx.lineTo(px + b.w * z + 2, py - 3 - z * 0.6);
      ctx.lineTo(px + b.w * z + 2 + z * 0.4, py - 3 - z * 0.45);
      ctx.lineTo(px + b.w * z + 2, py - 3 - z * 0.3);
      ctx.fill();
    }
  }

  private drawLabels(w: World, x0: number, y0: number, x1: number, y1: number, z: number, force: boolean) {
    const ctx = this.ctx;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const size = Math.round(Math.max(10, Math.min(14, z * 0.36)));
    ctx.font = `600 ${size}px "Space Grotesk Variable", ui-sans-serif, system-ui, sans-serif`;
    for (const b of w.buildings.values()) {
      if (b.kind === 'belt' || b.kind === 'firewall' || b.kind === 'router') continue;
      if (b.x + b.w < x0 || b.x > x1 || b.y + b.h < y0 || b.y > y1) continue;
      if (!w.revealed(b.x, b.y)) continue;
      if (!force && b.kind === 'breaker') continue;
      const [px, py] = this.cam.toScreen(b.x + b.w / 2, b.y + b.h);
      const tw = ctx.measureText(b.label).width;
      ctx.fillStyle = 'rgba(8,11,16,0.78)';
      roundRect(ctx, px - tw / 2 - 5, py + 2, tw + 10, size + 6, 5);
      ctx.fill();
      ctx.fillStyle = b.corrupted ? '#FF8AE6' : INK;
      ctx.fillText(b.label, px, py + 5);
    }
  }
}

export function rangeOf(kind: BuildingKind, w: World): number {
  switch (kind) {
    case 'rack':
      return TUNING.rackRadius;
    case 'monitor':
      return TUNING.monitorRadius * (w.techs.has('range') ? 1.4 : 1);
    case 'checkpoint':
      return TUNING.checkpointRadius;
    case 'scanner':
      return TUNING.scannerRadius;
    case 'drones':
      return TUNING.droneRange;
    default:
      return 0;
  }
}
